import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { fromJS, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { getMembers, type GetMembersQueryParams } from '@gonfalon/openapi/operations/getMembers';

import { createMember, type Member } from '#utils/members/records/Member';

export type MemberAccessCheck = NonNullable<NonNullable<GetMembersQueryParams['filter']>['accessCheck']>;

type RequestOptions = { signal?: AbortSignal };

const MAX_ACCESS_CHECK_PAGES = 5;

async function fetchMembersPage(query: GetMembersQueryParams, options: RequestOptions) {
  const { data, error, response } = await getMembers({ query }, options);
  if (!response.ok) {
    throw new RESTAPIError({
      status: response.status,
      url: response.url,
      code: 'unknown_error',
      message: response.statusText || `HTTP ${response.status}`,
      cause: error,
    });
  }
  return OrderedMap<string, Member>(
    (data?.items ?? []).map((item) => {
      const member = createMember(fromJS(item));
      return [member._id, member];
    }),
  );
}

export async function searchMembers(
  { query, limit, offset }: { query?: string; limit: number; offset?: number },
  options: RequestOptions = {},
): Promise<OrderedMap<string, Member>> {
  return fetchMembersPage({ filter: query ? { query } : undefined, limit, offset, sort: 'displayName' }, options);
}

export async function narrowMembersByAccessCheck(
  members: OrderedMap<string, Member>,
  accessCheck: MemberAccessCheck,
  options: RequestOptions = {},
): Promise<OrderedMap<string, Member>> {
  if (members.isEmpty()) {
    return members;
  }
  const ids = members.keySeq().toArray();
  const allowed = await fetchMembersPage({ filter: { ids, accessCheck }, limit: ids.length }, options);
  return members.filter((_, id) => allowed.has(id));
}

export async function collectMembersWithAccess(
  firstPage: OrderedMap<string, Member>,
  { query, pageSize, accessCheck }: { query?: string; pageSize: number; accessCheck: MemberAccessCheck },
  options: RequestOptions = {},
): Promise<OrderedMap<string, Member>> {
  let allowed = await narrowMembersByAccessCheck(firstPage, accessCheck, options);
  let page = firstPage;
  for (let index = 1; allowed.size < pageSize && page.size === pageSize && index < MAX_ACCESS_CHECK_PAGES; index++) {
    page = await searchMembers({ query, limit: pageSize, offset: index * pageSize }, options);
    allowed = allowed.merge(await narrowMembersByAccessCheck(page, accessCheck, options));
  }
  return allowed;
}
